import type { Locale, SectorDefinition, SectorDomain, Status } from '../types';
import { attainment, ragFor, type Rag } from './targets';

/** Composite sector score = mean of its regional composite indexes (0–100). */
export function compositeScore(sector: SectorDefinition, locale: Locale): number {
  const rows = sector.content[locale].regions.rows;
  if (rows.length === 0) return 0;
  return Math.round(rows.reduce((sum, r) => sum + r.index, 0) / rows.length);
}

/** National Readiness Index = mean of all sector composite scores (0–100). */
export function nationalIndex(sectors: SectorDefinition[], locale: Locale): number {
  if (sectors.length === 0) return 0;
  return Math.round(sectors.reduce((s, sec) => s + compositeScore(sec, locale), 0) / sectors.length);
}

/** Maps a 0–100 readiness score to a status band. */
export function statusFor(score: number): Status {
  if (score >= 85) return 'excellent';
  if (score >= 75) return 'good';
  if (score >= 65) return 'watch';
  return 'act';
}

/** Mean target attainment (%) across a domain's KPIs that carry targets. */
export function domainScore(
  sector: SectorDefinition,
  domain: SectorDomain,
  locale: Locale,
): number | undefined {
  const kpis = sector.content[locale].kpis.filter((k) => domain.kpis.includes(k.id));
  const vals = kpis.map((k) => attainment(k)).filter((v): v is number => v !== undefined);
  if (vals.length === 0) return undefined;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

/** RAG band for a domain (achieved/near/off), or undefined when no targets. */
export function domainRag(
  sector: SectorDefinition,
  domain: SectorDomain,
  locale: Locale,
): Rag | undefined {
  const score = domainScore(sector, domain, locale);
  return score === undefined ? undefined : ragFor(score);
}

/** Count of a sector's KPIs that are off-target (attainment < 90%). */
export function targetsAtRisk(sector: SectorDefinition, locale: Locale): number {
  return sector.content[locale].kpis.filter((k) => {
    const a = attainment(k);
    return a !== undefined && a < 90;
  }).length;
}

/** Count of critical alerts in a sector. */
export function criticalCount(sector: SectorDefinition, locale: Locale): number {
  return sector.content[locale].alerts.items.filter((a) => a.level === 'critical').length;
}

import { applySegments } from './segments';

/**
 * National index for the previous period (year segment 1), used for momentum.
 * Returns the current index when no prior-period data exists.
 */
export function nationalIndexPrev(sectors: SectorDefinition[], locale: Locale): number {
  const prev = sectors.map((s) => applySegments(s, { stage: 0, year: 1 }));
  return nationalIndex(prev, locale);
}
